import { useMemo, useRef, useState, useLayoutEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { RadarBubble } from '@/services/api/teacher'
import { useAnimationConfig } from '../hooks/useReducedMotion'
import { Portal } from './Portal'
import { RadarBubbleContent } from './RadarBubbleContent'

interface FloatingTooltipProps {
  bubble: RadarBubble | null
  position: { x: number; y: number }
  visible: boolean
  isAdminView?: boolean
}

const PADDING = 16
const CURSOR_OFFSET = 20
const MIN_SCALE = 0.6
const MIN_WIDTH = 200

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Floating Glass Tooltip — Measure-then-Place with Adaptive Scaling
 *
 * Renders the tooltip, measures its actual DOM size, then positions it
 * intelligently: flip/slide to stay in viewport, scale down if the content
 * is taller than the available space. Never scrolls — everything visible
 * at a glance.
 *
 * Phase B.1: Portal-Based Tooltip
 * Phase position-fix-v3: Measure-then-Place + transform-safe measurement
 */
export function FloatingTooltip({
  bubble,
  position,
  visible,
  isAdminView = false,
}: FloatingTooltipProps) {
  const animConfig = useAnimationConfig()
  const contentRef = useRef<HTMLDivElement>(null)
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 })

  // Measure actual tooltip size after render (synchronous, before paint).
  // Re-measure only when the bubble changes. Guard against render loops
  // by only updating state when dimensions actually change.
  //
  // IMPORTANT: Use offsetWidth/offsetHeight (NOT getBoundingClientRect).
  // offsetWidth/Height return layout dimensions unaffected by CSS transforms,
  // preventing an infinite loop when scale changes the visual size.
  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el || !bubble) return
    const w = el.offsetWidth
    const h = el.offsetHeight
    if (w === 0 || h === 0) return
    setMeasuredSize(prev => {
      if (prev.width === w && prev.height === h) return prev
      return { width: w, height: h }
    })
  }, [bubble?.id, bubble])

  // Calculate optimal placement: position + scale
  const placement = useMemo(() => {
    if (!bubble) return { x: 0, y: 0, scale: 1 }

    const vw = window.innerWidth
    const vh = window.innerHeight

    // Fallback to estimate if not yet measured
    const lessonCount = bubble.lessons?.length || 1
    const fallbackH = lessonCount > 1 ? 80 + lessonCount * 18 : 120
    const rawH = measuredSize.height > 0 ? measuredSize.height : fallbackH
    const rawW = Math.max(measuredSize.width > 0 ? measuredSize.width : MIN_WIDTH, MIN_WIDTH)

    const availH = vh - 2 * PADDING

    // Determine scale: shrink if too tall
    let scale = 1
    if (rawH > availH) {
      scale = Math.max(MIN_SCALE, availH / rawH)
    }

    const effH = rawH * scale
    const effW = rawW * scale

    // Horizontal: try right of cursor, flip to left if overflow
    let x = position.x + CURSOR_OFFSET
    if (x + effW > vw - PADDING) {
      x = position.x - effW - CURSOR_OFFSET
    }
    x = clamp(x, PADDING, vw - effW - PADDING)

    // Vertical: try above cursor (bottom edge near cursor), flip below if overflow
    let y = position.y - effH - CURSOR_OFFSET
    if (y < PADDING) {
      y = position.y + CURSOR_OFFSET
    }
    y = clamp(y, PADDING, vh - effH - PADDING)

    return { x, y, scale }
  }, [position, bubble, measuredSize])

  if (!bubble) return null

  return (
    <Portal>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
              duration: animConfig.reducedMotion ? 0 : undefined,
            }}
            style={{
              position: 'fixed',
              left: placement.x,
              top: placement.y,
              zIndex: 100,
              pointerEvents: 'none',
              minWidth: MIN_WIDTH,
            }}
          >
            <div
              ref={contentRef}
              style={{
                transform: `scale(${placement.scale})`,
                transformOrigin: 'top left',
              }}
            >
              <div
                className="rounded-xl p-3 border"
                style={{
                  background: 'var(--glass-tooltip-bg)',
                  backdropFilter: 'blur(var(--glass-tooltip-blur))',
                  WebkitBackdropFilter: 'blur(var(--glass-tooltip-blur))',
                  borderColor: 'var(--glass-tooltip-border)',
                  boxShadow: 'var(--glass-tooltip-shadow)',
                }}
              >
                <RadarBubbleContent bubble={bubble} isAdminView={isAdminView} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  )
}
